// Tab mentions let someone name a specific sheet tab inside an Automate prompt
// ("email me the new rows in @Orders"). There is no quoting syntax: tab names
// routinely contain spaces, and asking people to type @"Sales Data" reads worse
// than it parses. Instead every mention is matched against the tab names we
// already read from Google, longest name first, so "@Sales Data" beats a tab
// merely called "Sales" and anything unknown stays plain text.

const WORD_CHAR = /[A-Za-z0-9_]/;
const MAX_MENTION_QUERY = 40;

function normalizeName(name) {
  return String(name || '').trim().toLowerCase();
}

// A mention only starts where a word cannot already be running, so email
// addresses (me@example.com) and decorators never trigger one.
function isMentionStart(char) {
  if (char === undefined) return true;
  return !WORD_CHAR.test(char) && char !== '@';
}

// A mention only ends on a non-word character, so "@Orders" does not match
// inside "@OrdersArchive" — that tab, if it exists, wins on its own length.
function matchesAt(rest, token) {
  if (rest.length < token.length) return false;
  if (rest.slice(0, token.length).toLowerCase() !== token.toLowerCase()) return false;
  return !WORD_CHAR.test(rest[token.length] || ' ');
}

// Flattens every connected spreadsheet into the tabs a prompt can name. A tab
// name is qualified with its spreadsheet ("Budget 2026/Orders") only when it
// collides with a tab of the same name elsewhere; the primary spreadsheet keeps
// the bare name so the common case stays short.
export function buildMentionables(spreadsheets) {
  const counts = new Map();

  for (const sheet of spreadsheets || []) {
    for (const tab of sheet?.tabs || []) {
      const key = normalizeName(tab?.name);
      if (!key) continue;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }

  const list = [];

  for (const sheet of spreadsheets || []) {
    const sheetName = String(sheet?.name || 'Spreadsheet').trim();
    for (const tab of sheet?.tabs || []) {
      const tabName = String(tab?.name || '').trim();
      if (!tabName) continue;

      const qualified = !sheet?.primary && counts.get(normalizeName(tabName)) > 1;
      list.push({
        token: qualified ? `${sheetName}/${tabName}` : tabName,
        tabName,
        sheetName,
        sheetId: sheet?.id || null,
        headers: tab?.headers || [],
        primary: Boolean(sheet?.primary)
      });
    }
  }

  return list;
}

// Every mentionable named in the text, in the order it first appears.
export function findMentions(text, mentionables) {
  const source = String(text || '');
  if (!source.includes('@')) return [];

  const byLength = [...(mentionables || [])].sort((a, b) => b.token.length - a.token.length);
  const found = [];
  const seen = new Set();

  for (let i = 0; i < source.length; i += 1) {
    if (source[i] !== '@') continue;
    if (!isMentionStart(source[i - 1])) continue;

    const rest = source.slice(i + 1);
    const hit = byLength.find((mentionable) => matchesAt(rest, mentionable.token));
    if (!hit) continue;

    if (!seen.has(hit.token)) {
      seen.add(hit.token);
      found.push(hit);
    }
    i += hit.token.length;
  }

  return found;
}

// The half-typed "@quer" sitting just before the caret, if there is one.
// Returns the slice to replace when a suggestion is picked.
export function mentionQueryAt(text, caret) {
  const upto = String(text || '').slice(0, caret);
  const start = upto.lastIndexOf('@');
  if (start === -1) return null;
  if (!isMentionStart(upto[start - 1])) return null;

  const query = upto.slice(start + 1);
  if (query.length > MAX_MENTION_QUERY) return null;
  if (/[\n\r]/.test(query)) return null;

  return { start, end: caret, query };
}

// Prefix matches first, then substring. Callers close the menu on an empty
// result, which is what lets a query with spaces ("@Sales Data next week")
// stop suggesting once it stops looking like a tab name.
export function matchMentionables(mentionables, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return [...(mentionables || [])];

  const prefix = [];
  const substring = [];

  for (const mentionable of mentionables || []) {
    const token = mentionable.token.toLowerCase();
    const tab = mentionable.tabName.toLowerCase();
    if (token.startsWith(needle) || tab.startsWith(needle)) prefix.push(mentionable);
    else if (token.includes(needle) || tab.includes(needle)) substring.push(mentionable);
  }

  return [...prefix, ...substring];
}

// Writes "@Token " into the textarea, replacing `range` when the insert came
// from the autocomplete and inserting at the caret when it came from a chip.
export function insertMention(textarea, token, range = null) {
  const value = textarea.value || '';
  const start = range ? range.start : textarea.selectionStart ?? value.length;
  const end = range ? range.end : textarea.selectionEnd ?? start;

  const before = value.slice(0, start);
  const after = value.slice(end);
  // Chips insert mid-sentence, so keep a space in front unless one is there.
  const lead = before.length && !/\s$/.test(before) ? ' ' : '';
  const trail = /^\s/.test(after) ? '' : ' ';
  const insertion = `${lead}@${token}${trail}`;

  textarea.value = `${before}${insertion}${after}`;
  const caret = before.length + insertion.length;
  textarea.setSelectionRange(caret, caret);
  return textarea.value;
}
